import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import BenevolCheckLogo from '../components/BenevolCheckLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ValidateCertificatePage = () => {
  const { validationLink } = useParams();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validatorEmail, setValidatorEmail] = useState('');
  const [validating, setValidating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState('');
  const [competencies, setCompetencies] = useState({
    learning_ability: false,
    autonomy: false,
    adaptability: false,
    rigor: false,
    sociability: false,
    empathy: false,
    leadership: false,
    conflict_management: false,
    creativity: false,
    curiosity: false,
    perseverance: false,
    engagement: false
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const competencyCategories = [
    {
      title: 'Dans l\'action',
      items: [
        { key: 'learning_ability', label: 'Capacité d\'apprentissage', desc: 'il a su acquérir les savoir-faire nécessaires pour réaliser les tâches qui lui ont été confiées' },
        { key: 'autonomy', label: 'Autonomie', desc: 'il a exercé son activité sans avoir besoin d\'une supervision' },
        { key: 'adaptability', label: 'Adaptabilité', desc: 'il s\'est adapté facilement aux différentes situations rencontrées' },
        { key: 'rigor', label: 'Rigueur', desc: 'il a fait preuve d\'une exigence personnelle dans la réalisation des tâches qui lui ont été confiées' }
      ]
    },
    {
      title: 'En équipe',
      items: [
        { key: 'sociability', label: 'Sociabilité', desc: 'il s\'est intégré facilement dans l\'équipe et a noué de bonnes relations avec les autres' },
        { key: 'empathy', label: 'Empathie', desc: 'dans les moments de tension, il a su se mettre à la place de l\'autre pour comprendre son point de vue et éviter les conflits' },
        { key: 'leadership', label: 'Leadership', desc: 'il a montré des capacités pour motiver l\'activité des autres et les solliciter en leur apportant si nécessaire un conseil ou un appui' },
        { key: 'conflict_management', label: 'Gestion des conflits', desc: 'il a su résoudre les situations de désaccord en trouvant des compromis' }
      ]
    },
    {
      title: 'Dans l\'engagement',
      items: [
        { key: 'creativity', label: 'Créativité', desc: 'il a proposé des idées nouvelles pour améliorer le fonctionnement ou les activités' },
        { key: 'curiosity', label: 'Curiosité', desc: 'il a manifesté un intérêt pour comprendre le sens et les enjeux de l\'action menée' },
        { key: 'perseverance', label: 'Persévérance', desc: 'face aux difficultés, il n\'a pas renoncé et a cherché des solutions pour les surmonter' },
        { key: 'engagement', label: 'Engagement', desc: 'il s\'est montré prêt à prendre des responsabilités dans l\'animation, la vie collective ou le développement de l\'association' }
      ]
    }
  ];

  useEffect(() => {
    fetchCertificate();
  }, [validationLink]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/certificates/validate/${validationLink}`);
      setCertificate(response.data);
      setCompetencies(response.data.competencies || competencies);
      setComment(response.data.comment || '');
      // Initialiser les dates
      if (response.data.start_date) {
        setStartDate(response.data.start_date.split('T')[0]);
      }
      if (response.data.end_date) {
        setEndDate(response.data.end_date.split('T')[0]);
      }
      
      if (response.data.status === 'validated') {
        setSuccess(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Cette attestation n\'existe plus ou a déjà été traitée. Si vous pensez qu\'il s\'agit d\'une erreur, veuillez contacter le bénévole pour qu\'il renvoie une nouvelle demande de validation.');
      } else {
        setError('Lien de validation invalide ou expiré. Veuillez demander au bénévole de renvoyer la demande de validation.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompetencyToggle = (key) => {
    setCompetencies(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/certificates/validate/${validationLink}`, {
        competencies,
        comment,
        start_date: startDate,
        end_date: endDate
      });
      setEditMode(false);
      // Update local certificate state
      setCertificate(prev => ({
        ...prev,
        competencies,
        comment,
        start_date: startDate,
        end_date: endDate
      }));
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (e) => {
    e.preventDefault();
    setError('');
    setValidating(true);

    try {
      // Always save competencies, comment and dates before validation
      await axios.put(`${API}/certificates/validate/${validationLink}`, {
        competencies,
        comment,
        start_date: startDate,
        end_date: endDate
      });

      await axios.post(
        `${API}/certificates/${certificate.id}/validate`,
        {
          certificate_id: certificate.id,
          validator_email: validatorEmail
        }
      );
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la validation');
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <p>Chargement...</p>
      </div>
    );
  }

  if (!certificate || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertDescription>{error || 'Attestation introuvable'}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedCompetencies = Object.entries(competencies).filter(([_, v]) => v);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Card data-testid="validation-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <BenevolCheckLogo size="lg" />
            </div>
            <CardTitle className="text-2xl">
              {success ? 'Attestation validée !' : 'Validation d\'attestation'}
            </CardTitle>
            <CardDescription>
              {success 
                ? 'L\'attestation a été validée avec succès' 
                : 'Vérifiez, modifiez si nécessaire et validez l\'attestation ci-dessous'
              }
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {success ? (
              <Alert className="bg-green-50 border-green-200" data-testid="success-alert">
                <AlertDescription className="text-green-800">
                  ✅ L'attestation a été validée avec succès. Le bénévole recevra une notification par email. Vous allez être redirigé...
                </AlertDescription>
              </Alert>
            ) : certificate.status === 'validated' ? (
              <Alert className="bg-blue-50 border-blue-200" data-testid="already-validated-alert">
                <AlertDescription className="text-blue-800">
                  ℹ️ Cette attestation a déjà été validée le {new Date(certificate.validation_date).toLocaleDateString('fr-FR')}
                </AlertDescription>
              </Alert>
            ) : null}

            {/* Certificate Preview */}
            <div className="bg-gray-50 p-6 rounded-lg space-y-4" data-testid="certificate-preview">
              <h3 className="font-semibold text-lg border-b pb-2">📋 Détails de l'attestation</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Bénévole</p>
                  <p className="font-medium">{certificate.volunteer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{certificate.volunteer_email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Association</p>
                  <p className="font-medium">{certificate.association_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Responsable</p>
                  <p className="font-medium">{certificate.signatory_name}</p>
                </div>
                {editMode ? (
                  <>
                    <div>
                      <Label htmlFor="start_date" className="text-gray-500">Date de début</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="mt-1"
                        data-testid="start-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date" className="text-gray-500">Date de fin</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="mt-1"
                        data-testid="end-date-input"
                      />
                    </div>
                  </>
                ) : (
                  <div>
                    <p className="text-gray-500">Période</p>
                    <p className="font-medium">Du {new Date(certificate.start_date).toLocaleDateString('fr-FR')} au {new Date(certificate.end_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">Volume horaire</p>
                  <p className="font-medium text-indigo-600">{certificate.hours_volume} heures</p>
                </div>
              </div>
              <div>
                <p className="text-gray-500">Mission</p>
                <p className="font-medium">{certificate.mission_description}</p>
              </div>
            </div>

            {/* Competencies Section */}
            {!success && certificate.status !== 'validated' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">🎯 Compétences acquises</h3>
                  <Button 
                    variant={editMode ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setEditMode(!editMode)}
                    data-testid="edit-competencies-button"
                  >
                    {editMode ? '✓ Mode édition actif' : '✏️ Modifier les compétences'}
                  </Button>
                </div>

                {editMode ? (
                  <div className="space-y-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      📝 Vous pouvez ajouter ou retirer des compétences en cliquant dessus
                    </p>
                    {competencyCategories.map((category) => (
                      <div key={category.title} className="space-y-2">
                        <h4 className="font-semibold text-sm text-indigo-900">{category.title}</h4>
                        <div className="grid gap-2">
                          {category.items.map((comp) => (
                            <button
                              key={comp.key}
                              type="button"
                              onClick={() => handleCompetencyToggle(comp.key)}
                              className={`text-left p-3 rounded-lg border transition-all ${
                                competencies[comp.key]
                                  ? 'bg-green-100 border-green-300 text-green-800'
                                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                              data-testid={`competency-${comp.key}`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{competencies[comp.key] ? '✅' : '⬜'}</span>
                                <div>
                                  <p className="font-medium text-sm">{comp.label}</p>
                                  <p className="text-xs opacity-75">{comp.desc}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    <div className="pt-4 border-t border-yellow-300">
                      <Button 
                        onClick={handleSaveChanges}
                        disabled={saving}
                        className="w-full"
                        data-testid="save-changes-button"
                      >
                        {saving ? '💾 Enregistrement...' : '💾 Enregistrer les modifications'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-lg border">
                    {selectedCompetencies.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedCompetencies.map(([key]) => {
                          const comp = competencyCategories.flatMap(c => c.items).find(i => i.key === key);
                          return comp ? (
                            <span 
                              key={key}
                              className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                            >
                              ✅ {comp.label}
                            </span>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucune compétence sélectionnée</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Comment Section */}
            {!success && certificate.status !== 'validated' && (
              <div className="space-y-2">
                <Label htmlFor="comment">💬 Commentaire (optionnel)</Label>
                <Textarea
                  id="comment"
                  placeholder="Ajoutez un commentaire ou une appréciation sur le bénévole..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  data-testid="comment-input"
                />
                <p className="text-xs text-gray-500">
                  Ce commentaire apparaîtra sur l'attestation finale
                </p>
              </div>
            )}

            {/* Display comment if exists and validated */}
            {certificate.comment && (success || certificate.status === 'validated') && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-blue-800">💬 Commentaire du responsable :</p>
                <p className="text-sm text-blue-700 mt-1">{certificate.comment}</p>
              </div>
            )}

            {/* Validation Form */}
            {!success && certificate.status !== 'validated' && (
              <form onSubmit={handleValidate} className="space-y-4 pt-4 border-t">
                {error && (
                  <Alert variant="destructive" data-testid="error-alert">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="validator_email">Votre email (responsable associatif)</Label>
                  <Input
                    id="validator_email"
                    type="email"
                    placeholder="responsable@association.fr"
                    value={validatorEmail}
                    onChange={(e) => setValidatorEmail(e.target.value)}
                    required
                    data-testid="validator-email-input"
                  />
                  <p className="text-sm text-gray-600">
                    Veuillez entrer votre email pour confirmer que vous êtes autorisé à valider cette attestation.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={validating}
                  data-testid="validate-button"
                >
                  {validating ? 'Validation en cours...' : '✅ Valider l\'attestation'}
                </Button>
              </form>
            )}
          </CardContent>

          <CardFooter className="justify-center">
            <Button 
              variant="link" 
              onClick={() => navigate('/')}
              data-testid="home-link"
            >
              Retour à l'accueil
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ValidateCertificatePage;
